import { useState } from "react";
import { FolderPlus, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, ApiError, type Mailbox } from "../../lib/api-client";
import { mailboxLabel } from "../../lib/mailbox-labels";
import logoUrl from "../../assets/berry_mail_logo.svg";

interface MailboxNavProps {
  mailboxes: Mailbox[] | null;
  selectedMailboxId?: string;
  error: string | null;
  onCompose: () => void;
  onMailboxCreated: (mailbox: Mailbox) => void;
  onMailboxDeleted: (mailboxId: string) => void;
  // Solo en mobile (ver MailLayout): el nav se muestra en un drawer que hay
  // que cerrar después de elegir una carpeta o de abrir "Redactar" — en
  // desktop no se pasa y el nav se queda como columna fija de siempre.
  onNavigate?: () => void;
}

const ROLE_ORDER = ["inbox", "drafts", "sent", "junk", "trash"];

export default function MailboxNav({
  mailboxes,
  selectedMailboxId,
  error,
  onCompose,
  onMailboxCreated,
  onMailboxDeleted,
  onNavigate,
}: MailboxNavProps) {
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function handleDeleteFolder(e: React.MouseEvent, mailbox: Mailbox) {
    e.preventDefault();
    e.stopPropagation();
    if (deletingId) return;
    if (!window.confirm(`¿Eliminar la carpeta "${mailboxLabel(mailbox)}"?`)) return;

    setDeletingId(mailbox.id);
    try {
      await api.deleteMailbox(mailbox.id);
      onMailboxDeleted(mailbox.id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const force = window.confirm(
          `La carpeta "${mailboxLabel(mailbox)}" todavía tiene correos. ¿Eliminarla junto con esos correos? Esta acción no se puede deshacer.`,
        );
        if (force) {
          try {
            await api.deleteMailbox(mailbox.id, true);
            onMailboxDeleted(mailbox.id);
          } catch {
            window.alert("No se pudo eliminar la carpeta.");
          }
        }
      } else {
        window.alert("No se pudo eliminar la carpeta.");
      }
    } finally {
      setDeletingId(null);
    }
  }

  async function handleCreateFolder(e: React.FormEvent) {
    e.preventDefault();
    const name = newFolderName.trim();
    if (!name || isCreating) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      const mailbox = await api.createMailbox({ name });
      onMailboxCreated(mailbox);
      setNewFolderName("");
      setIsCreatingFolder(false);
    } catch (err) {
      setCreateError(
        err instanceof ApiError ? "No se pudo crear la carpeta." : "No se pudo crear la carpeta.",
      );
    } finally {
      setIsCreating(false);
    }
  }

  const sorted = mailboxes
    ? [...mailboxes].sort((a, b) => {
        const ra = ROLE_ORDER.indexOf(a.role ?? "");
        const rb = ROLE_ORDER.indexOf(b.role ?? "");
        if (ra === -1 && rb === -1) return a.name.localeCompare(b.name);
        if (ra === -1) return 1;
        if (rb === -1) return -1;
        return ra - rb;
      })
    : null;

  return (
    <nav
      style={{
        background: "var(--sidebar-bg)",
        display: "flex",
        flexDirection: "column",
        padding: "16px 12px",
        gap: 4,
        minHeight: 0,
        // height:100% (no solo el stretch implícito de grid/flex): en el
        // drawer mobile este <nav> vive dentro de un contenedor con
        // position:fixed cuya altura viene de top+bottom, no de un padre
        // flex/grid — sin esto quedaría alto 0 (ver MailLayout).
        height: "100%",
        overflowY: "auto",
      }}
    >
      <div style={{ marginBottom: 20, padding: "0 4px" }}>
        {/* Logo pre-coloreado en blanco (src/assets/berry_mail_logo.svg) —
            pensado para el fondo sólido de marca del sidebar, no cambia con
            el tema claro/oscuro de la app (ver --sidebar-bg en tokens.css). */}
        <img src={logoUrl} alt="BerryLoo Mail" style={{ height: 28, width: "auto" }} />
      </div>

      <button
        onClick={() => {
          onCompose();
          onNavigate?.();
        }}
        style={{
          background: "var(--sidebar-accent)",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          height: 36,
          fontSize: 13,
          fontWeight: 500,
          marginBottom: 12,
          cursor: "pointer",
        }}
      >
        Redactar
      </button>

      {error && (
        <p style={{ fontSize: 12, color: "#ffb4c4", padding: "0 4px" }}>
          No se pudieron cargar los mailboxes: {error}
        </p>
      )}

      {sorted?.map((mb) => (
        <Link
          key={mb.id}
          to={`/mailbox/${mb.id}`}
          onClick={() => onNavigate?.()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 10px",
            borderRadius: 8,
            fontSize: 13,
            textDecoration: "none",
            color: mb.id === selectedMailboxId ? "var(--sidebar-active-fg)" : "var(--sidebar-text-muted)",
            background: mb.id === selectedMailboxId ? "var(--sidebar-active-bg)" : "transparent",
            fontWeight: mb.id === selectedMailboxId ? 500 : 400,
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {mailboxLabel(mb)}
          </span>
          {!!mb.unreadEmails && (
            <span style={{ marginLeft: "auto", fontSize: 11 }}>{mb.unreadEmails}</span>
          )}
          {/* Solo carpetas de usuario (sin role) se pueden borrar — Inbox,
              Sent, Drafts, Junk, Trash y Archive son de sistema. */}
          {!mb.role && (
            <button
              type="button"
              onClick={(e) => handleDeleteFolder(e, mb)}
              disabled={deletingId === mb.id}
              title="Eliminar carpeta"
              style={{
                display: "flex",
                marginLeft: mb.unreadEmails ? 4 : "auto",
                border: "none",
                background: "transparent",
                color: "var(--sidebar-text-muted)",
                cursor: deletingId === mb.id ? "default" : "pointer",
                padding: 2,
                flexShrink: 0,
              }}
            >
              <X size={12} />
            </button>
          )}
        </Link>
      ))}

      {isCreatingFolder ? (
        <form onSubmit={handleCreateFolder} style={{ padding: "4px 4px 0", display: "flex", flexDirection: "column", gap: 6 }}>
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Nombre de la carpeta"
            autoFocus
            disabled={isCreating}
            style={{
              border: "0.5px solid var(--sidebar-text-muted)",
              background: "transparent",
              borderRadius: 6,
              color: "var(--sidebar-text)",
              fontSize: 12,
              padding: "6px 8px",
              outline: "none",
            }}
          />
          {createError && <span style={{ fontSize: 11, color: "#ffb4c4" }}>{createError}</span>}
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="submit"
              disabled={isCreating || !newFolderName.trim()}
              style={{
                flex: 1,
                border: "none",
                background: "var(--sidebar-accent)",
                color: "#fff",
                borderRadius: 6,
                fontSize: 11,
                padding: "5px 8px",
                cursor: isCreating ? "default" : "pointer",
              }}
            >
              Crear
            </button>
            <button
              type="button"
              onClick={() => {
                setIsCreatingFolder(false);
                setNewFolderName("");
                setCreateError(null);
              }}
              disabled={isCreating}
              style={{
                flex: 1,
                border: "0.5px solid var(--sidebar-text-muted)",
                background: "transparent",
                color: "var(--sidebar-text-muted)",
                borderRadius: 6,
                fontSize: 11,
                padding: "5px 8px",
                cursor: isCreating ? "default" : "pointer",
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setIsCreatingFolder(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 10px",
            borderRadius: 8,
            fontSize: 13,
            border: "none",
            background: "transparent",
            color: "var(--sidebar-text-muted)",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <FolderPlus size={14} /> Nueva carpeta
        </button>
      )}
    </nav>
  );
}
